import { FinancingConfigDto } from '@applyonline/shared'
import { FORM_ID } from '@applyonline/shared/components'
import { useFormAutoSave } from '@applyonline/shared/utils'
import { yupResolver } from '@hookform/resolvers/yup'
import { formatCurrencyNoDecimals } from '@if/core/utils'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import NumberInput from '../../../components/NumberInputProps'
import { buildLoanAmountInputSchema, LoanAmountInput } from '../../../types/Schemas/PrequalificationSchema'
import InvisalignQuestion from './InvisalignQuestion'

interface Props {
  prequalificationData: LoanAmountInput
  financingProgramConfig: FinancingConfigDto
  onPrequalificationChanged: (data: LoanAmountInput) => void
  nextStepAfter: (currentStep: string) => void
  currentStep: string
  showInvisalignQuestion: boolean
}

const LoanAmountForm = ({
  prequalificationData,
  financingProgramConfig,
  onPrequalificationChanged,
  nextStepAfter,
  currentStep,
  showInvisalignQuestion,
}: Props) => {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const {
    handleSubmit,
    control,
    formState: { errors },
    subscribe,
    watch,
    setValue,
  } = useForm<LoanAmountInput>({
    mode: 'onBlur',
    resolver: yupResolver(buildLoanAmountInputSchema(financingProgramConfig)),
    defaultValues: prequalificationData,
  })

  useFormAutoSave(subscribe, onPrequalificationChanged)

  const onSubmit: SubmitHandler<LoanAmountInput> = (data) => {
    onPrequalificationChanged(data)
    nextStepAfter(currentStep)
  }

  return (
    <section className="step-content">
      <h3 className="form-question">{t('loanAmount.desiredAmount')}</h3>
      <div className="paragraph">
        <p>
          <Trans
            i18nKey="loanAmount.amountThresholds"
            values={{
              minLoanAmount: formatCurrencyNoDecimals(financingProgramConfig.minimumLoanAmount),
              maxLoanAmount: formatCurrencyNoDecimals(financingProgramConfig.maximumLoanAmount),
            }}
          />
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID} noValidate>
        <Controller
          name="requestedLoanAmount"
          control={control}
          render={({ field }) => (
            <NumberInput
              {...field}
              id="requestedLoanAmountInput"
              error={errors.requestedLoanAmount}
              label={t('loanAmount.amount')}
              wrapStyle={lang === 'en' ? 'number-wrap startwith' : 'number-wrap endwith'}
            />
          )}
        />

        {showInvisalignQuestion && (
          <InvisalignQuestion
            loanPurposeId={watch('loanPurposeId')}
            value={watch('isInvisalign') ?? false}
            onChange={(v) => setValue('isInvisalign', v, { shouldDirty: true })}
          />
        )}
      </form>
    </section>
  )
}

export default LoanAmountForm
